import emojiRegex from "emoji-regex";
import iconv from "iconv-lite";
import { PDFField, PDFForm, PDFTextField } from "pdf-lib";

/**
 * Before `updateFieldAppearances(StandardFonts.Helvetica)`, field text must fit
 * **WinAnsi** (pdf-lib aligns this with Windows-1252). Rich Unicode (e.g. **U+2010**
 * `‐`) is not encodable; **U+002D** `-` is — hence `WinAnsi cannot encode` crashes.
 * **Emoji** are stripped first (**emoji-regex**); remaining text is round-tripped via
 * **iconv-lite** (unmappable chars become `?`).
 *
 * **Why Standard Helvetica:** Rehab / docs PDF code sets it explicitly, e.g.
 * `const font = await pdfDoc.embedFont(StandardFonts.Helvetica);` then
 * `form.updateFieldAppearances(font)` (see `useCreateRehaPdf`, `useCreateFormPdf`,
 * `useCreateTransitionalCarePdf`). That is a deliberate trade-off: **pdf-lib** Standard
 * 14 fonts need no embedded TTF and match typical official forms, but text is limited
 * to **WinAnsi**. Use a custom `embedFont(fontBytes)` + `updateFieldAppearances` if you need
 * full Unicode or branded typography instead of sanitizing.
 *
 * Limitations:
 * - **Emoji:** removed (no glyph in Standard Helvetica); adjacent spaces are left as-is
 *   (e.g. `"a 😀 b"` → `"a  b"`). They are not rendered on the PDF.
 * - **Other non-WinAnsi text** (CJK, RTL, many math/special symbols): not stripped;
 *   **iconv-lite** replaces unmappable code points with `?`.
 * - **Faithful Unicode** (emoji on the page, full CJK, etc.) needs an embedded font and
 *   a different `updateFieldAppearances` path — not this helper.
 * - Rare mismatches between pdf-lib’s WinAnsi table and **iconv** are possible in edge
 *   cases.
 *
 * @see https://recare.atlassian.net/browse/DEV-19972
 */

const WIN1252 = "windows-1252";

const EMOJI_PATTERN = emojiRegex();

function stripEmojis(text: string): string {
  return text.replace(EMOJI_PATTERN, "");
}

/** WinAnsi-safe string for Standard 14 font appearances (see module comment). */
export function sanitizeTextForStandardPdfFont(text: string): string {
  const withoutEmoji = stripEmojis(text);
  return iconv.decode(iconv.encode(withoutEmoji, WIN1252), WIN1252);
}

function isPdfTextField(field: PDFField): field is PDFTextField {
  return field instanceof PDFTextField;
}

/** After pdf fillers, before `embedFont` + `updateFieldAppearances` (see module). */
export function sanitizePdfFormTextFieldsForStandardFont(form: PDFForm): void {
  for (const field of form.getFields()) {
    if (!isPdfTextField(field)) continue;
    const text = field.getText();
    if (!text?.length) continue;
    const sanitized = sanitizeTextForStandardPdfFont(text);
    if (sanitized !== text) {
      field.setText(sanitized);
    }
  }
}
